import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function SpecialRuleManager({ api, getAuthHeader }) {
  const [specialRules, setSpecialRules] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    nationality_id: '',
    visa_type_id: '',
    rule_text: '',
    is_restricted: false
  });

  useEffect(() => {
    fetchSpecialRules();
    fetchNationalities();
    fetchVisaTypes();
  }, []);

  const fetchSpecialRules = async () => {
    try {
      const response = await axios.get(`${api}/special-rules`);
      setSpecialRules(response.data);
    } catch (error) {
      toast.error('Failed to fetch special rules');
    }
  };

  const fetchNationalities = async () => {
    try {
      const response = await axios.get(`${api}/nationalities`);
      setNationalities(response.data);
    } catch (error) {
      toast.error('Failed to fetch nationalities');
    }
  };

  const fetchVisaTypes = async () => {
    try {
      const response = await axios.get(`${api}/visa-types`);
      setVisaTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch visa types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${api}/special-rules/${editingId}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Special rule updated successfully');
      } else {
        await axios.post(`${api}/special-rules`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Special rule added successfully');
      }
      resetForm();
      fetchSpecialRules();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleEdit = (rule) => {
    setFormData({
      nationality_id: rule.nationality_id,
      visa_type_id: rule.visa_type_id,
      rule_text: rule.rule_text,
      is_restricted: rule.is_restricted || false
    });
    setEditingId(rule.id);
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this special rule?')) return;
    try {
      await axios.delete(`${api}/special-rules/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Special rule deleted successfully');
      fetchSpecialRules();
    } catch (error) {
      toast.error('Failed to delete special rule');
    }
  };

  const resetForm = () => {
    setFormData({
      nationality_id: '',
      visa_type_id: '',
      rule_text: '',
      is_restricted: false
    });
    setEditingId(null);
    setShowAdd(false);
  };

  const getNationalityName = (id) => {
    const nat = nationalities.find((n) => n.id === id);
    return nat ? nat.nationality_name : 'Unknown';
  };

  const getVisaTypeName = (id) => {
    const visa = visaTypes.find((v) => v.id === id);
    return visa ? visa.visa_type : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Manage Special Rules</h2>
          <p className="text-slate-600 mt-1">Total: {specialRules.length}</p>
        </div>
        <Button
          data-testid="add-special-rule-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="btn-primary flex items-center gap-2"
        >
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAdd ? 'Cancel' : 'Add Special Rule'}
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Special Rule' : 'Add New Special Rule'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label-text">Nationality *</label>
                  <Select
                    onValueChange={(value) => setFormData({ ...formData, nationality_id: value })}
                    value={formData.nationality_id}
                  >
                    <SelectTrigger data-testid="special-rule-nationality-select" className="input-field">
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {nationalities.map((nat) => (
                        <SelectItem key={nat.id} value={nat.id}>
                          {nat.nationality_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-text">Visa Type *</label>
                  <Select
                    onValueChange={(value) => setFormData({ ...formData, visa_type_id: value })}
                    value={formData.visa_type_id}
                  >
                    <SelectTrigger data-testid="special-rule-visa-type-select" className="input-field">
                      <SelectValue placeholder="Select visa type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visaTypes.map((visa) => (
                        <SelectItem key={visa.id} value={visa.id}>
                          {visa.visa_type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="label-text">Rule Text *</label>
                <Textarea
                  data-testid="special-rule-text-input"
                  value={formData.rule_text}
                  onChange={(e) => setFormData({ ...formData, rule_text: e.target.value })}
                  className="input-field min-h-[120px]"
                  placeholder="e.g., Pakistan nationals need 6 months bank statement with AED 5000 balance"
                  required
                />
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <Switch
                  data-testid="special-rule-restricted-switch"
                  checked={formData.is_restricted}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_restricted: checked })}
                />
                <label className="text-sm font-medium text-slate-700">
                  <strong>Restricted Visa Type</strong> - Cannot process this visa (shows "Contact Us" message instead of prices)
                </label>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="btn-primary flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Add'}
                </Button>
                <Button type="button" onClick={resetForm} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <div className="grid gap-4">
        {specialRules.map((rule) => (
          <Card key={rule.id} className={`hover:shadow-lg transition-shadow border-l-4 ${rule.is_restricted ? 'border-red-500' : 'border-orange-500'}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                      {getNationalityName(rule.nationality_id)}
                    </span>
                    <span className="text-slate-400">→</span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                      {getVisaTypeName(rule.visa_type_id)}
                    </span>
                    {rule.is_restricted && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        🚫 RESTRICTED
                      </span>
                    )}
                  </div>
                  <p className="text-slate-700 leading-relaxed">{rule.rule_text}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    data-testid={`edit-special-rule-${rule.id}`}
                    onClick={() => handleEdit(rule)}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-special-rule-${rule.id}`}
                    onClick={() => handleDelete(rule.id)}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
